import { Menu } from '@headlessui/react';
import { InboxIcon } from '@heroicons/react/24/outline';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/solid';
import { Link } from '@remix-run/react';

import Typography from '~/components/Typography';

import type { NotificationType } from '~/interfaces/notification';

import renderTime from '~/utils/renderTime';

import { useWebThemeContext } from '../Layout/Context/useWebThemeContext';
import MarkDownRender from '../Markdown/MarkDownRender';
import NotificationsLoader from './NotificationsLoader';
import useReadNotifications from './hooks/useReadNotifications';

interface Props {
  notifications: NotificationType[];
  isLoading: boolean;
  textColor?: string;
}

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
const NotificationsList = ({ notifications, isLoading, textColor }: Props) => {
  const { handleReadNotification } = useReadNotifications();
  const { webTheme } = useWebThemeContext();
  const navbarTextColor = webTheme?.navbar_text_color;

  if (isLoading) {
    return (
      <div className="p-2">
        <NotificationsLoader />
      </div>
    );
  }

  const hasNotifications = notifications.length > 0;
  if (!hasNotifications) {
    return (
      <div className="flex h-full flex-col items-center justify-center space-y-2 p-2">
        <Typography as="span" color={navbarTextColor} fontFamilyv2={webTheme?.navbar_font_family}>
          <InboxIcon className="mx-auto h-6 w-6" />
        </Typography>
        <Typography as="span" color={navbarTextColor} fontFamilyv2={webTheme?.navbar_font_family}>
          No new notifications
        </Typography>
      </div>
    );
  }

  return (
    <ul className="divide-y">
      {notifications.map((notification: NotificationType) => {
        const readableTime = renderTime(notification.created_at);

        let slug: string;
        try {
          const notificationUrl = new URL(notification.url);
          slug = notificationUrl.pathname + notificationUrl.search;
        } catch (error) {
          slug = '/';
          console.error(error);
        }

        return (
          <li key={notification.id} className="relative first:rounded-t-md last:rounded-b-md">
            <Menu.Item>
              <Link
                to={slug}
                onClick={() => handleReadNotification(notification.id)}
                className="flex w-full flex-row space-x-2 px-3 py-3 hover:underline"
              >
                <div
                  className="pt-1"
                  style={{
                    color: navbarTextColor,
                  }}
                >
                  <ChatBubbleLeftRightIcon className="h-4 w-4" />
                </div>
                <div className="flex w-full flex-col space-y-2">
                  <Typography
                    as="span"
                    color={navbarTextColor}
                    fontFamilyv2={webTheme?.navbar_font_family}
                    className="text-sm sm:block"
                  >
                    <MarkDownRender>{notification.message}</MarkDownRender>
                  </Typography>
                  <div className="flex flex-row space-x-2">
                    <Typography
                      as="span"
                      color={navbarTextColor}
                      fontFamilyv2={webTheme?.navbar_font_family}
                      className="text-xs sm:block"
                    >
                      <small className="font-regular my-auto text-xs">{readableTime}</small>
                    </Typography>
                  </div>
                </div>
              </Link>
            </Menu.Item>
          </li>
        );
      })}
    </ul>
  );
};

export default NotificationsList;
